// Sentry bootstrap for the web app.
//
// Design notes:
// - Init is gated on VITE_SENTRY_DSN. If unset, every public export is a no-op.
//   This keeps local dev and CI free of telemetry without any code changes.
// - PII is scrubbed aggressively in `beforeSend`: Authorization headers,
//   cookies, and any query/body key that looks like a secret (`token`,
//   `password`, `apiKey`, etc.) are replaced with `[REDACTED]`.
// - `sendDefaultPii: false` ensures Sentry doesn't auto-attach IPs or cookies.
// - The captured release ties crashes to a specific deploy via
//   VITE_APP_VERSION (apps/web/package.json "version", set by CI; the SHA is VITE_GIT_SHA).
//
// COLD START: the SDK is loaded with a DYNAMIC import, never a static one.
//   `@sentry/*` is ~509 KB of source — the single largest thing in the entry
//   chunk (~20% of it) — and it is not needed to paint the first frame. A
//   static import put that parse+compile cost on the critical path of every
//   cold start, on every platform, *even when VITE_SENTRY_DSN is unset* (both
//   native env files ship it empty, so phones were compiling half a megabyte
//   of telemetry code that then did nothing at all).
//
//   Consequence: there is now a window between boot and the SDK resolving.
//   `captureError` / `setUser` buffer during that window and flush on load, so
//   nothing is dropped — the public API of this module is unchanged and every
//   caller stays synchronous.

import type * as SentryNS from "@sentry/react";
import type { ErrorInfo } from "react";
import { APP_ENV, APP_VERSION, GIT_SHA } from "./runtime-env";

type ViteEnv = {
  VITE_SENTRY_DSN?: string;
  VITE_SENTRY_TRACES_SAMPLE_RATE?: string;
  VITE_APP_VERSION?: string;
  MODE?: string;
};

function readEnv(): ViteEnv {
  // Vite exposes env via `import.meta.env`; access through a typed-safe cast
  // so the fallback still compiles under Jest/Vitest or SSR.
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (import.meta as any).env ?? {};
  } catch {
    return {};
  }
}

const SECRET_KEY = /(authori[sz]ation|cookie|set-cookie|password|token|secret|api[_-]?key|refresh[_-]?token)/i;

function scrubObject<T>(value: T): T {
  if (!value || typeof value !== "object") return value;
  if (Array.isArray(value)) {
    return value.map((item) => scrubObject(item)) as T;
  }
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (SECRET_KEY.test(k)) {
      out[k] = "[REDACTED]";
    } else if (v && typeof v === "object") {
      out[k] = scrubObject(v);
    } else {
      out[k] = v;
    }
  }
  return out as T;
}

let initialized = false;
/** Resolved SDK namespace. `null` until the dynamic import lands. */
let Sentry: typeof SentryNS | null = null;
/** Set as soon as we know a DSN exists, i.e. "telemetry is coming up". */
let enabling = false;

// Buffered while the SDK is in flight. Bounded so a boot-time error loop can
// never grow this without limit; the first errors are the diagnostic ones.
const MAX_BUFFERED_ERRORS = 25;
const bufferedErrors: Array<{ err: unknown; info?: ErrorInfo }> = [];
// Only the LAST user matters — replaying intermediate identities is noise.
let bufferedUser: Parameters<typeof setUser>[0] | undefined;
let hasBufferedUser = false;

function flushBuffers(): void {
  if (!Sentry) return;
  if (hasBufferedUser) {
    hasBufferedUser = false;
    const u = bufferedUser;
    bufferedUser = undefined;
    setUser(u ?? null);
  }
  const queued = bufferedErrors.splice(0, bufferedErrors.length);
  for (const { err, info } of queued) captureError(err, info);
}

export function initSentry(): void {
  if (initialized || enabling) return;
  const env = readEnv();
  const dsn = env.VITE_SENTRY_DSN;
  if (!dsn) return;
  enabling = true;

  // Deliberately fire-and-forget: boot must not await telemetry. A failed
  // import (offline cold start, blocked CDN) leaves the app fully functional
  // with buffering disabled.
  void import("@sentry/react")
    .then((mod) => {
      Sentry = mod;
      configure(mod, dsn, env);
      initialized = true;
      flushBuffers();
    })
    .catch(() => {
      // Telemetry is best-effort. Drop the buffer so it can't leak memory.
      enabling = false;
      bufferedErrors.length = 0;
      hasBufferedUser = false;
      bufferedUser = undefined;
    });
}

function configure(Sentry: typeof SentryNS, dsn: string, env: ViteEnv): void {
  const tracesSampleRate = Number(env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? "0.05");

  Sentry.init({
    dsn,
    // Tag every event with the explicit env name (dev/staging/production)
    // so the Sentry dashboard can filter prod from staging crashes.
    // Falls back to MODE for pure-web builds where APP_ENV="web".
    environment: APP_ENV !== "web" ? APP_ENV : env.MODE ?? "production",
    // Release format: "ai.campushub.app@<version>+<sha>". Sentry parses
    // this into project + version + dist for sourcemap matching.
    release: `ai.campushub.app@${APP_VERSION}+${GIT_SHA}`,
    tracesSampleRate: Number.isFinite(tracesSampleRate) ? tracesSampleRate : 0.05,
    sendDefaultPii: false,
    integrations: [Sentry.browserTracingIntegration()],
    // Don't flood Sentry with ResizeObserver spam or cancelled fetches.
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      "AbortError",
    ],
    beforeSend(event) {
      if (event.request) {
        event.request = scrubObject(event.request);
      }
      if (event.contexts) {
        event.contexts = scrubObject(event.contexts);
      }
      if (event.extra) {
        event.extra = scrubObject(event.extra);
      }
      return event;
    },
  });
}

export function captureError(err: unknown, info?: ErrorInfo): void {
  if (!Sentry) {
    // Buffer only while an init is genuinely in flight. With no DSN this stays
    // a pure no-op, exactly as before.
    if (enabling && bufferedErrors.length < MAX_BUFFERED_ERRORS) {
      bufferedErrors.push({ err, info });
    }
    return;
  }
  const sdk = Sentry;
  sdk.withScope((scope) => {
    if (info?.componentStack) {
      scope.setContext("react", { componentStack: info.componentStack });
    }
    sdk.captureException(err);
  });
}

export function setUser(user: { id: string; role?: string; schoolId?: string | null } | null): void {
  if (!Sentry) {
    if (enabling) {
      bufferedUser = user;
      hasBufferedUser = true;
    }
    return;
  }
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    id: user.id,
    segment: user.role,
    schoolId: user.schoolId ?? undefined,
  });
}
